import React from 'react';
import { 
  Building2, 
  Image, 
  Palette, 
  FileText, 
  Globe, 
  Mail, 
  Briefcase, 
  ShieldX,
  CheckCircle
} from 'lucide-react';
import { OnboardingFormData } from '../OnboardingWizardContent';

interface Props {
  data: OnboardingFormData;
}

const SPECIALISATION_OPTIONS: Record<string, string> = {
  'tax-returns': 'Tax Returns & Compliance',
  'bookkeeping': 'Bookkeeping & Payroll',
  'business-advisory': 'Business Advisory',
  'financial-planning': 'Financial Planning',
  'audit-assurance': 'Audit & Assurance',
  'gst-compliance': 'GST Compliance',
  'company-formations': 'Company Formations',
  'trust-accounting': 'Trust Accounting',
  'property-investment': 'Property Investment',
  'estate-planning': 'Estate Planning',
  'kiwisaver': 'KiwiSaver & Retirement',
  'xero-certified': 'Xero Certified Partner',
};

const StepReview: React.FC<Props> = ({ data }) => {
  const ReviewSection: React.FC<{ 
    icon: React.ReactNode; 
    title: string; 
    children: React.ReactNode;
    completed?: boolean;
  }> = ({ icon, title, children, completed = true }) => (
    <div className="border border-border rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {icon}
          <h4 className="font-medium text-foreground">{title}</h4>
        </div>
        {completed && <CheckCircle className="h-4 w-4 text-brand-teal" />}
      </div>
      <div className="text-sm text-muted-foreground">{children}</div>
    </div>
  );

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground mb-6">
        Please review your information before submitting. You can go back to any step 
        to make changes.
      </p>

      <ReviewSection 
        icon={<Building2 className="h-5 w-5 text-brand-teal" />}
        title="Company Details"
      >
        <p><strong>Name:</strong> {data.companyName}</p>
        <p><strong>Industry:</strong> {data.industry}</p>
      </ReviewSection>

      <ReviewSection 
        icon={<Image className="h-5 w-5 text-brand-teal" />}
        title="Brand Kit"
        completed={data.useWebsiteAsDesignReference || !!data.logoFile || !!data.brandGuidelineFile}
      >
        {data.useWebsiteAsDesignReference ? (
          <p className="text-brand-teal">✓ Using website design as design reference</p>
        ) : null}
        {data.logoFile ? (
          <p>✓ Logo uploaded: {data.logoFile.name}</p>
        ) : !data.useWebsiteAsDesignReference ? (
          <p className="text-muted-foreground/60">No logo uploaded</p>
        ) : null}
        {data.brandGuidelineFile ? (
          <p>✓ Brand guidelines uploaded: {data.brandGuidelineFile.name}</p>
        ) : !data.useWebsiteAsDesignReference ? (
          <p className="text-muted-foreground/60">No brand guidelines uploaded</p>
        ) : null}
      </ReviewSection>

      <ReviewSection 
        icon={<Palette className="h-5 w-5 text-brand-teal" />}
        title="Brand Colors & Fonts"
        completed={!!data.primaryColor || data.fontFiles.length > 0}
      >
        {data.primaryColor && (
          <div className="flex items-center gap-2">
            <span>Primary:</span>
            <div 
              className="w-4 h-4 rounded" 
              style={{ backgroundColor: data.primaryColor }}
            />
            <span className="font-mono text-xs">{data.primaryColor}</span>
          </div>
        )}
        {data.secondaryColor && (
          <div className="flex items-center gap-2">
            <span>Secondary:</span>
            <div 
              className="w-4 h-4 rounded" 
              style={{ backgroundColor: data.secondaryColor }}
            />
            <span className="font-mono text-xs">{data.secondaryColor}</span>
          </div>
        )}
        {data.fontFiles.length > 0 && (
          <p>✓ {data.fontFiles.length} font(s) uploaded</p>
        )}
        {!data.primaryColor && !data.secondaryColor && data.fontFiles.length === 0 && (
          <p className="text-muted-foreground/60">Will be extracted from brand kit</p>
        )}
      </ReviewSection>

      <ReviewSection 
        icon={<FileText className="h-5 w-5 text-brand-teal" />}
        title="Documents & FAQs"
        completed={data.documentFiles.length > 0}
      >
        {data.documentFiles.length > 0 ? (
          <p>✓ {data.documentFiles.length} document(s) uploaded</p>
        ) : (
          <p className="text-muted-foreground/60">No documents uploaded</p>
        )}
      </ReviewSection>

      <ReviewSection 
        icon={<Globe className="h-5 w-5 text-brand-teal" />}
        title="Website URLs"
      >
        <p><strong>Primary:</strong> {data.primaryWebsiteUrl}</p>
        {data.additionalUrls.filter(u => u.trim()).length > 0 && (
          <p><strong>Additional:</strong> {data.additionalUrls.filter(u => u.trim()).join(', ')}</p>
        )}
      </ReviewSection>

      <ReviewSection 
        icon={<Mail className="h-5 w-5 text-brand-teal" />}
        title="Developer & Installation"
      >
        <p><strong>Developer Email:</strong> {data.developerEmail}</p>
        <p><strong>Install Scope:</strong> {data.installScope === 'site-wide' ? 'Site-wide' : 'Specific pages'}</p>
      </ReviewSection>

      <ReviewSection 
        icon={<Mail className="h-5 w-5 text-brand-teal" />}
        title="Summary Emails"
      >
        <p><strong>Forward to:</strong> {data.summaryForwardEmail}</p>
      </ReviewSection>

      <ReviewSection 
        icon={<Briefcase className="h-5 w-5 text-brand-teal" />}
        title="Specialisations"
        completed={data.specialisations.length > 0 || !!data.specialisationsOther}
      >
        {data.specialisations.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {data.specialisations.map(s => (
              <span key={s} className="px-2 py-0.5 bg-brand-teal/10 text-brand-teal text-xs rounded">
                {SPECIALISATION_OPTIONS[s] || s}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground/60">None specified</p>
        )}
        {data.specialisationsOther && (
          <p className="mt-1"><strong>Other:</strong> {data.specialisationsOther}</p>
        )}
      </ReviewSection>

      <ReviewSection 
        icon={<ShieldX className="h-5 w-5 text-brand-teal" />}
        title="Topics to Avoid"
        completed={!!data.doNotMention}
      >
        {data.doNotMention ? (
          <p className="whitespace-pre-wrap">{data.doNotMention}</p>
        ) : (
          <p className="text-muted-foreground/60">No custom restrictions</p>
        )}
      </ReviewSection>

      <div className="bg-brand-teal/5 border border-brand-teal/20 rounded-lg p-4 mt-6">
        <p className="text-sm text-card-foreground">
          <strong>🚀 What happens next?</strong> Once you submit, our team will begin 
          building your assistant. We'll email you when it's ready for review (usually 
          within 7-14 days).
        </p>
      </div>
    </div>
  );
};

export default StepReview;
